import { gql } from '@apollo/client';

const RECENT_FRAGMENT = gql`
  fragment RecentProjectsFragment on ProjectConnection {
    pageInfo {
      hasNextPage
      endCursor
    }
    edges {
      node {
        id
        archivedAt
        databaseId
        formattedLine1
        formattedLine2
        lastActivityAt
        photoCount
        videosCount
        documentCount
        viewerFavorited
        hasCollaborations
        metadata
        hasShowcaseProject
        featuredPhoto {
          annotatedUris(variants: [MEDIUM]) {
            uri
          }
        }
        owningCollaborator {
          ... on ProjectCollaboratorInterface {
            id
            __typename
          }
        }
        tags {
          edges {
            node {
              id
              displayValue
              color
              databaseId
            }
          }
        }
      }
    }
  }
`;

export const USER_RECENT_PROJECTS_QUERY = gql`
  query RecentProjects(
    $cursor: String
    $query: String
    $tags: [String!]
    $users: [ID!]
    $createdAfter: ISO8601DateTime
    $createdBefore: ISO8601DateTime
  ) {
    viewer {
      id
      recentProjects(
        first: 10
        after: $cursor
        query: $query
        tags: $tags
        users: $users
        createdAfter: $createdAfter
        createdBefore: $createdBefore
      ) @connection(key: "recent-projects") {
        ...RecentProjectsFragment
      }
    }
  }
  ${RECENT_FRAGMENT}
`;

export const COMPANY_ACTIVE_PROJECTS_QUERY = gql`
  query ActiveProjects(
    $cursor: String
    $query: String
    $tags: [String!]
    $users: [ID!]
    $groups: [ID!]
    $createdAfter: ISO8601DateTime
    $createdBefore: ISO8601DateTime
  ) {
    viewer {
      id
      company {
        id
        activeProjects(
          first: 10
          after: $cursor
          query: $query
          tags: $tags
          users: $users
          groups: $groups
          createdAfter: $createdAfter
          createdBefore: $createdBefore
        ) @connection(key: "company-recent-projects") {
          ...RecentProjectsFragment
        }
      }
    }
  }
  ${RECENT_FRAGMENT}
`;

export const USER_FAVORITED_PROJECTS_QUERY = gql`
  query RecentProjects(
    $cursor: String
    $query: String
    $tags: [String!]
    $users: [ID!]
    $groups: [ID!]
    $createdAfter: ISO8601DateTime
    $createdBefore: ISO8601DateTime
  ) {
    viewer {
      id
      favoritedProjects(
        first: 10
        after: $cursor
        query: $query
        tags: $tags
        users: $users
        groups: $groups
        createdAfter: $createdAfter
        createdBefore: $createdBefore
      ) @connection(key: "favorited-projects") {
        ...RecentProjectsFragment
      }
    }
  }
  ${RECENT_FRAGMENT}
`;

export const ARCHIVED_PROJECTS_QUERY = gql`
  query ArchivedProjects(
    $cursor: String
    $query: String
    $tags: [String!]
    $users: [ID!]
    $groups: [ID!]
    $createdAfter: ISO8601DateTime
    $createdBefore: ISO8601DateTime
  ) {
    viewer {
      id
      company {
        id
        archivedProjects(
          first: 10
          after: $cursor
          query: $query
          tags: $tags
          users: $users
          groups: $groups
          createdAfter: $createdAfter
          createdBefore: $createdBefore
        ) @connection(key: "company-archived-projects") {
          ...RecentProjectsFragment
        }
      }
    }
  }
  ${RECENT_FRAGMENT}
`;

const PROJECT_LABELS_FRAGMENT = gql`
  fragment ProjectLabels on TagConnection {
    edges {
      node {
        id
        databaseId
        displayValue
        color
      }
    }
  }
`;

export const PROJECT_LABELS_QUERY = gql`
  query ProjectTags($id: ID!) {
    viewer {
      id
      project(id: $id) {
        id
        tags {
          ...ProjectLabels
        }
      }
    }
  }

  ${PROJECT_LABELS_FRAGMENT}
`;

export const ALTER_LABELS_MUTATION = gql`
  mutation AlterTags($input: RecordablesAlterTagsInput!) {
    recordablesAlterTags(input: $input) {
      recordables {
        ... on Project {
          id
          tags {
            ...ProjectLabels
          }
        }
      }
    }
  }

  ${PROJECT_LABELS_FRAGMENT}
`;

export const PROJECT_FEATURED_PHOTO_QUERY = gql`
  query GetProject($nodeId: ID!) {
    node(id: $nodeId) {
      id
      __typename
      ... on Project {
        featuredPhoto {
          __typename
          id
          databaseId
          annotatedUris(variants: [THUMBNAIL, MEDIUM, ORIGINAL]) {
            variant
            uri
          }
        }
      }
    }
  }
`;

export const PROJECT_CAN_VIEWER_INVITE_COLLABORATOR_QUERY = gql`
  query GetProjectCanViewerInviteCollaborator($nodeId: ID!) {
    node(id: $nodeId) {
      id
      __typename
      ... on Project {
        canCurrentViewerInviteCollaborator
      }
    }
  }
`;

export const PROJECT_COLLAB_DATA_QUERY = gql`
  query GetProjectCollaborationData($nodeId: ID!) {
    viewer {
      id
      project(id: $nodeId) {
        id
        __typename
        owningCollaborator {
          ... on ProjectCollaboratorInterface {
            id
            __typename
            name
            avatar
          }
        }
        hasCollaborations
      }
    }
  }
`;
